import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "@/context/AuthContext";
import { toast } from "sonner";
import { Rocket, Loader2, Plug, CheckCircle2, XCircle, Trash2, RefreshCw, ExternalLink, Link2, Stethoscope, Wand2, GitPullRequest, ShieldCheck, Undo2, Activity, Bell, BellOff, LogIn, UploadCloud } from "lucide-react";

const STATE_COLOR = { live: "text-emerald-400", building: "text-[#00F0FF]", queued: "text-slate-400", error: "text-red-400" };

export default function DeployPanel({ projectId, githubUrl }) {
  const [providers, setProviders] = useState([]);
  const [planned, setPlanned] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [creds, setCreds] = useState({});
  const [busy, setBusy] = useState("");
  const [diagnoses, setDiagnoses] = useState({});
  const [health, setHealth] = useState({});
  const [compare, setCompare] = useState(null);
  const [prCfg, setPrCfg] = useState(null);
  const pollers = useRef({});

  const loadProviders = useCallback(async () => {
    try { const d = await apiFetch("/deploy/providers"); setProviders(d.providers); setPlanned(d.planned); } catch {}
  }, []);
  const [oauthCfg, setOauthCfg] = useState({});
  const loadOauth = useCallback(async () => {
    try { const d = await apiFetch("/deploy/oauth/providers"); setOauthCfg(Object.fromEntries((d.providers || []).map((p) => [p.provider, p.configured]))); } catch {}
  }, []);
  const loadDeployments = useCallback(async () => {
    try { setDeployments(await apiFetch(`/deploy/projects/${projectId}/deployments`)); } catch {}
  }, [projectId]);
  const [envKeys, setEnvKeys] = useState([]);
  const [envKey, setEnvKey] = useState("");
  const [envVal, setEnvVal] = useState("");
  const loadEnv = useCallback(async () => {
    try { const d = await apiFetch(`/deploy/projects/${projectId}/env`); setEnvKeys(d.keys || []); } catch {}
  }, [projectId]);

  const addEnv = async () => {
    if (!envKey.trim() || !envVal) { toast.error("Enter a key and value."); return; }
    setBusy("env");
    try { const d = await apiFetch(`/deploy/projects/${projectId}/env`, { method: "PUT", body: { vars: { [envKey.trim()]: envVal } } });
      setEnvKeys(d.keys); setEnvKey(""); setEnvVal(""); toast.success("Environment variable saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const delEnv = async (k) => {
    try { await apiFetch(`/deploy/projects/${projectId}/env/${encodeURIComponent(k)}`, { method: "DELETE" });
      setEnvKeys((ks) => ks.filter((x) => x !== k)); toast.success(`Removed ${k}`);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { loadProviders(); loadDeployments(); loadEnv(); loadOauth(); }, [loadProviders, loadDeployments, loadEnv, loadOauth]);

  // Handle the OAuth callback redirect (?oauth=connected|failed&provider=…)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get("oauth");
    if (!oauth) return;
    const prov = params.get("provider") || "";
    if (oauth === "connected") { toast.success(`Connected ${prov} via OAuth`); loadProviders(); }
    else { toast.error(`${prov} OAuth failed. Please try again.`); }
    params.delete("oauth"); params.delete("provider"); params.delete("reason");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, [loadProviders]);

  const startOAuth = async (key) => {
    setBusy(`oauth-${key}`);
    try { const r = await apiFetch(`/deploy/oauth/${key}/start`, { method: "POST" }); window.location.assign(r.authorize_url); }
    catch (e) { toast.error(e.message); setBusy(""); }
  };

  const [syncing, setSyncing] = useState("");
  const [prSetup, setPrSetup] = useState(null);
  const syncEnv = async (key, name) => {
    setSyncing(key);
    try { const r = await apiFetch(`/deploy/projects/${projectId}/env/sync`, { method: "POST", body: { provider: key } });
      toast.success(`Synced ${r.synced} variable(s) to ${name}`, { description: (r.logs || [])[0] });
    } catch (e) { toast.error(e.message); } finally { setSyncing(""); }
  };

  const poll = useCallback((id) => {
    if (pollers.current[id]) return;
    pollers.current[id] = setInterval(async () => {
      try {
        const s = await apiFetch(`/deploy/deployments/${id}`);
        setDeployments((ds) => ds.map((d) => (d.id === id ? s : d)));
        if (["live", "error"].includes(s.state)) { clearInterval(pollers.current[id]); delete pollers.current[id]; }
      } catch {}
    }, 3000);
  }, []);

  useEffect(() => {
    deployments.forEach((d) => { if (["queued", "building"].includes(d.state)) poll(d.id); });
    return () => { Object.values(pollers.current).forEach(clearInterval); pollers.current = {}; };
  }, [deployments, poll]);

  const connect = async (p) => {
    const fields = p.fields && p.fields.length ? p.fields : [{ key: "token", label: "API Token" }];
    const primary = (creds[fields[0].key] || "").trim();
    if (!primary) { toast.error(`Enter your ${fields[0].label}.`); return; }
    const extra = {};
    fields.slice(1).forEach((f) => { extra[f.key] = (creds[f.key] || "").trim(); });
    setBusy("connect");
    try {
      const r = await apiFetch("/deploy/connections", { method: "POST", body: { provider: p.key, token: primary, extra } });
      toast.success(`${p.name} connected as ${r.account || "your account"}`); setConnecting(null); setCreds({}); await loadProviders();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const disconnect = async (key) => {
    try { await apiFetch(`/deploy/connections/${key}`, { method: "DELETE" }); toast.success("Disconnected."); await loadProviders(); }
    catch (e) { toast.error(e.message); }
  };

  const launch = async (key, environment = "production") => {
    setBusy(`launch-${key}-${environment}`);
    try { const d = await apiFetch(`/deploy/projects/${projectId}/launch`, { method: "POST", body: { provider: key, environment } });
      toast.success(`Deploying ${environment} to ${key}…`); setDeployments((ds) => [d, ...ds]); poll(d.id);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const promote = async (id) => {
    setBusy(`promote-${id}`);
    try { const d = await apiFetch(`/deploy/deployments/${id}/promote`, { method: "POST" });
      toast.success("Promoting preview to production…"); setDeployments((ds) => [d, ...ds]); poll(d.id); loadDeployments();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const retry = async (id) => {
    try { const d = await apiFetch(`/deploy/deployments/${id}/retry`, { method: "POST" });
      setDeployments((ds) => ds.map((x) => (x.id === id ? d : x))); poll(id);
    } catch (e) { toast.error(e.message); }
  };

  const rollback = async (id) => {
    setBusy(`rollback-${id}`);
    try { const d = await apiFetch(`/deploy/deployments/${id}/rollback`, { method: "POST" });
      toast.success("Rolling back to the previous production version…"); setDeployments((ds) => [d, ...ds]); poll(d.id);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const checkHealth = async (id) => {
    setBusy(`health-${id}`);
    try { const h = await apiFetch(`/deploy/deployments/${id}/health`);
      setHealth((m) => ({ ...m, [id]: h }));
      h.status === "up" ? toast.success(`Healthy · ${h.latency_ms}ms`) : toast.error(`Down · ${h.error || h.http_status || "no response"}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const toggleMonitor = async (d) => {
    const enabled = !d.monitor_enabled;
    try { await apiFetch(`/deploy/deployments/${d.id}/monitor`, { method: "POST", body: { enabled } });
      setDeployments((ds) => ds.map((x) => (x.id === d.id ? { ...x, monitor_enabled: enabled } : x)));
      toast.success(enabled ? "Uptime monitoring on — you'll be emailed if it goes down" : "Monitoring off");
    } catch (e) { toast.error(e.message); }
  };

  const testConn = async (key) => {
    setBusy(`test-${key}`);
    try { const r = await apiFetch(`/deploy/connections/${key}/test`, { method: "POST" });
      r.ok ? toast.success(`${key} connection OK — ${r.account || "verified"}`) : toast.error(`${key}: ${r.detail}`);
      await loadProviders();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const diagnose = async (id) => {
    setBusy(`diag-${id}`);
    try { const r = await apiFetch(`/deploy/deployments/${id}/diagnose`, { method: "POST" });
      setDiagnoses((m) => ({ ...m, [id]: r }));
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const autofix = async (id) => {
    setBusy(`fix-${id}`);
    try { const r = await apiFetch(`/deploy/deployments/${id}/autofix`, { method: "POST" });
      toast.success(`AI Doctor applied '${r.applied_fix}' and is retrying`);
      setDeployments((ds) => ds.map((x) => (x.id === id ? r.deployment : x))); poll(id);
      setDiagnoses((m) => { const n = { ...m }; delete n[id]; return n; });
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const loadCompare = useCallback(async () => {
    try { setCompare(await apiFetch(`/deploy/projects/${projectId}/compare`)); } catch {}
  }, [projectId]);
  const loadPr = useCallback(async () => {
    try { setPrCfg(await apiFetch(`/deploy/projects/${projectId}/pr-previews`)); } catch {}
  }, [projectId]);
  useEffect(() => { loadCompare(); loadPr(); }, [loadCompare, loadPr, deployments]);

  const enablePr = async (provider) => {
    setBusy("pr");
    try { const r = await apiFetch(`/deploy/projects/${projectId}/pr-previews`, { method: "POST", body: { provider } });
      setPrSetup(r); await loadPr();
      if (r.webhook_created) toast.success("PR previews enabled — webhook auto-created on GitHub");
      else toast.success("PR previews enabled", { description: r.auto_error || "Add the webhook shown below on GitHub." });
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const disablePr = async () => {
    setBusy("pr");
    try { await apiFetch(`/deploy/projects/${projectId}/pr-previews`, { method: "DELETE" }); setPrSetup(null); await loadPr(); toast.success("PR previews disabled"); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const copyText = (t) => { try { navigator.clipboard.writeText(t); toast.success("Copied"); } catch { toast.error("Copy failed"); } };
  const githubConnected = providers.some((p) => p.key === "github" && p.connected);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importReplace, setImportReplace] = useState(false);
  const importEnv = async () => {
    if (!importText.trim()) { toast.error("Paste your .env content first"); return; }
    setBusy("import");
    try {
      const r = await apiFetch(`/deploy/projects/${projectId}/env/import`, { method: "POST", body: { content: importText, replace: importReplace } });
      toast.success(`Imported ${r.imported} variable(s)`, { description: `${r.count} total now stored (encrypted).` });
      setImportText(""); setShowImport(false); await loadEnv();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="studio-deploy">
      <p className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Rocket size={18} /> Deploy to production</p>
      <p className="text-slate-500 text-xs mb-4">Connect a hosting account and deploy this project with the provider's official API. Tokens are encrypted and never shared.</p>

      <div className="grid sm:grid-cols-2 gap-3" data-testid="deploy-providers">
        {providers.map((p) => (
          <div key={p.key} className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid={`deploy-provider-${p.key}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-white flex items-center gap-2">{p.name}
                  {p.connected && p.status !== "error" && <span className="text-emerald-400 text-[10px] font-mono inline-flex items-center gap-1"><CheckCircle2 size={12} /> connected</span>}
                  {p.connected && p.status === "error" && <span className="text-red-400 text-[10px] font-mono inline-flex items-center gap-1"><XCircle size={12} /> reconnect</span>}
                </p>
                <p className="text-slate-500 text-[11px] mt-0.5">{p.kind === "source" ? "Source & publishing" : !p.deployable ? "Credential vault · deploys via CI/CD" : p.needs_repo ? "Deploys from GitHub" : "Deploys your files"}{p.account ? ` · ${p.account}` : ""}{p.connected && p.auth_method === "oauth" ? " · OAuth" : ""}</p>
              </div>
              {p.connected ? (
                <div className="flex items-center gap-2">
                  {p.deployable ? (
                    <>
                      <button onClick={() => launch(p.key, "preview")} disabled={busy.startsWith(`launch-${p.key}`) || (p.needs_repo && !githubUrl)} title={p.needs_repo && !githubUrl ? "Publish to GitHub first" : ""} className="inline-flex items-center gap-1.5 border border-white/15 text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 hover:border-[#0055FF] transition-colors duration-300" data-testid={`deploy-preview-${p.key}`}>
                        {busy === `launch-${p.key}-preview` ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />} Preview
                      </button>
                      <button onClick={() => launch(p.key, "production")} disabled={busy.startsWith(`launch-${p.key}`) || (p.needs_repo && !githubUrl)} title={p.needs_repo && !githubUrl ? "Publish to GitHub first" : ""} className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 hover:bg-[#0047d6] transition-colors duration-300" data-testid={`deploy-launch-${p.key}`}>
                        {busy === `launch-${p.key}-production` ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />} Deploy
                      </button>
                    </>
                  ) : (
                    <span className="text-emerald-400 text-[11px] font-mono">{p.kind === "source" ? "ready for publishing" : "credentials stored"}</span>
                  )}
                  <button onClick={() => testConn(p.key)} disabled={busy === `test-${p.key}`} title="Test connection" className="text-slate-400 hover:text-[#00F0FF] transition-colors duration-300" data-testid={`deploy-test-${p.key}`}>{busy === `test-${p.key}` ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={15} />}</button>
                  <button onClick={() => disconnect(p.key)} className="text-slate-500 hover:text-red-400 transition-colors duration-300" data-testid={`deploy-disconnect-${p.key}`}><Trash2 size={15} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {p.oauth && (
                    <button
                      onClick={() => oauthCfg[p.key] ? startOAuth(p.key) : toast.message("OAuth not configured yet", { description: "Add this provider's OAuth client id/secret to the backend to enable one-click sign-in. You can connect with an API token now." })}
                      disabled={busy === `oauth-${p.key}`}
                      title={oauthCfg[p.key] ? `Sign in with ${p.name}` : "OAuth not configured yet — use an API token"}
                      className="inline-flex items-center gap-1.5 bg-white text-black text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#00F0FF] transition-colors duration-300 disabled:opacity-40"
                      data-testid={`deploy-oauth-${p.key}`}>
                      {busy === `oauth-${p.key}` ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />} Sign in{oauthCfg[p.key] ? "" : " (setup)"}
                    </button>
                  )}
                  <button onClick={() => setConnecting(connecting === p.key ? null : p.key)} className="inline-flex items-center gap-1.5 border border-white/15 text-xs px-3 py-2 rounded-lg hover:border-[#0055FF] transition-colors duration-300" data-testid={`deploy-connect-${p.key}`}><Plug size={13} /> {p.oauth ? "Token" : "Connect"}</button>
                </div>
              )}
            </div>
            {connecting === p.key && (
              <div className="mt-3">
                <p className="text-slate-500 text-[11px] mb-2">{p.docs}</p>
                <div className="space-y-2">
                  {(p.fields && p.fields.length ? p.fields : [{ key: "token", label: "API Token", secret: true }]).map((f) => (
                    <input
                      key={f.key}
                      value={creds[f.key] || ""}
                      onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                      type={f.secret ? "password" : "text"}
                      placeholder={`${f.label}${f.placeholder ? ` — ${f.placeholder}` : ""}`}
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0055FF]"
                      data-testid={`deploy-field-${p.key}-${f.key}`}
                    />
                  ))}
                  <button onClick={() => connect(p)} disabled={busy === "connect"} className="w-full inline-flex items-center justify-center gap-1.5 bg-white text-black text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 hover:bg-[#00F0FF] transition-colors duration-300" data-testid={`deploy-save-${p.key}`}>{busy === "connect" ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} Verify &amp; connect</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {planned.length > 0 && (
        <p className="text-slate-600 text-[11px] mt-3">Coming soon: {planned.map((p) => p.name).join(", ")} — credential vault ready.</p>
      )}

      {/* Compare preview vs production */}
      {compare && (compare.preview || compare.production) && (
        <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="deploy-compare">
          <div className="rounded-xl border border-purple-500/25 bg-purple-500/5 p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300 mb-1">Latest preview</p>
            {compare.preview ? <a href={compare.preview.url || "#"} target="_blank" rel="noreferrer" className="text-[#00F0FF] text-xs break-all" data-testid="compare-preview-url">{compare.preview.url || compare.preview.state}</a> : <p className="text-slate-500 text-xs">None yet</p>}
          </div>
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Production</p>
            {compare.production ? <a href={compare.production.url || "#"} target="_blank" rel="noreferrer" className="text-[#00F0FF] text-xs break-all" data-testid="compare-production-url">{compare.production.url || compare.production.state}</a> : <p className="text-slate-500 text-xs">None yet</p>}
          </div>
        </div>
      )}

      {/* Automatic PR previews */}
      {githubUrl && (
        <div className="mt-5 rounded-xl border border-white/10 bg-black/40 p-4" data-testid="deploy-pr-previews">
          <p className="font-semibold text-white text-sm flex items-center gap-2"><GitPullRequest size={15} /> Automatic PR previews</p>
          {prCfg?.config?.enabled ? (
            <div className="mt-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-emerald-400 text-xs">Enabled via {prCfg.config.provider} for {prCfg.config.repo_full_name}.{prCfg.config.webhook_id ? " Webhook active on GitHub." : " Add the webhook on GitHub to activate."} Pushes to <span className="font-mono">main</span> auto-deploy to production; every pull request gets a preview URL (torn down on close).</p>
                <button onClick={disablePr} disabled={busy === "pr"} className="text-xs border border-white/15 rounded-lg px-3 py-1.5 hover:border-red-400 hover:text-red-400 transition-colors duration-300 shrink-0" data-testid="deploy-pr-disable">Disable</button>
              </div>
              {prSetup && !prSetup.webhook_created && (
                <div className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 space-y-2" data-testid="pr-webhook-instructions">
                  <p className="text-amber-300 text-[11px] font-semibold">{prSetup.github_connected ? "Auto-setup unavailable — add the webhook manually:" : "Add this webhook on GitHub (Settings → Webhooks → Add webhook):"}</p>
                  <div className="flex items-center gap-2 text-[11px]"><span className="text-slate-500 w-24">Payload URL</span><code className="flex-1 font-mono text-slate-200 break-all">{prSetup.webhook_url}</code><button onClick={() => copyText(prSetup.webhook_url)} className="text-[#00F0FF]" data-testid="pr-copy-url">Copy</button></div>
                  <div className="flex items-center gap-2 text-[11px]"><span className="text-slate-500 w-24">Secret</span><code className="flex-1 font-mono text-slate-200 break-all">{prSetup.secret}</code><button onClick={() => copyText(prSetup.secret)} className="text-[#00F0FF]" data-testid="pr-copy-secret">Copy</button></div>
                  <p className="text-slate-500 text-[10px]">Content type: application/json · Events: Pull requests. This secret is shown once — copy it now.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <p className="text-slate-500 text-xs flex-1">Deploy a preview automatically for every GitHub pull request{githubConnected ? " (webhook auto-created on GitHub)" : ""}.</p>
              {providers.filter((p) => p.deployable && p.connected).map((p) => (
                <button key={p.key} onClick={() => enablePr(p.key)} disabled={busy === "pr"} className="text-xs border border-white/15 rounded-lg px-3 py-1.5 hover:border-[#0055FF] transition-colors duration-300" data-testid={`deploy-pr-enable-${p.key}`}>{busy === "pr" ? "…" : `Enable (${p.name})`}</button>
              ))}
            </div>
          )}
          {prCfg?.pr_deployments?.length > 0 && (
            <div className="mt-2 space-y-1">
              {prCfg.pr_deployments.slice(0, 5).map((d) => (
                <div key={d.id} className="text-xs text-slate-400 flex items-center gap-2"><span>PR #{d.pr_number}</span>{d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-[#00F0FF]">{d.url.replace("https://", "")}</a>}<span className={STATE_COLOR[d.state]}>{d.state}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Environment variables (encrypted, injected at deploy time across any provider) */}
      <div className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="env-vars-panel">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5"><ShieldCheck size={13} /> Environment variables · encrypted · applied to every provider</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={envKey} onChange={(e) => setEnvKey(e.target.value.toUpperCase())} placeholder="KEY" className="w-40 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-[#0055FF]" data-testid="env-key-input" />
          <input value={envVal} onChange={(e) => setEnvVal(e.target.value)} type="password" placeholder="value" className="flex-1 min-w-[160px] bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0055FF]" data-testid="env-value-input" />
          <button onClick={addEnv} disabled={busy === "env"} className="inline-flex items-center gap-1.5 bg-white text-black text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-40 hover:bg-[#00F0FF] transition-colors duration-300" data-testid="env-add-btn">{busy === "env" ? <Loader2 size={13} className="animate-spin" /> : "Add"}</button>
          <button onClick={() => setShowImport((s) => !s)} className="inline-flex items-center gap-1.5 border border-white/15 text-xs px-3 py-2 rounded-lg hover:border-[#0055FF] transition-colors duration-300" data-testid="env-import-toggle"><UploadCloud size={13} /> Import .env</button>
        </div>
        {showImport && (
          <div className="mb-3 rounded-xl border border-white/10 bg-black/40 p-3 space-y-2" data-testid="env-import-panel">
            <p className="text-slate-500 text-[11px]">Paste a .env file to bulk-import or rotate secrets. Existing keys are overwritten; values are encrypted at rest.</p>
            <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={5} placeholder={"DATABASE_URL=postgres://...\nJWT_SECRET=...\nSTRIPE_KEY=sk_live_..."} className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono outline-none focus:border-[#0055FF]" data-testid="env-import-textarea" />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer" data-testid="env-import-replace-label">
                <input type="checkbox" checked={importReplace} onChange={(e) => setImportReplace(e.target.checked)} data-testid="env-import-replace" /> Replace all existing variables
              </label>
              <button onClick={importEnv} disabled={busy === "import"} className="inline-flex items-center gap-1.5 bg-white text-black text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-40 hover:bg-[#00F0FF] transition-colors duration-300" data-testid="env-import-btn">{busy === "import" ? <Loader2 size={13} className="animate-spin" /> : "Import & encrypt"}</button>
            </div>
          </div>
        )}
        {envKeys.length === 0 ? (
          <p className="text-slate-600 text-xs">No variables yet. Add secrets like DATABASE_URL, JWT_SECRET or API keys — they are encrypted at rest and injected at deploy time.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="env-keys-list">
            {envKeys.map((k) => (
              <span key={k} className="inline-flex items-center gap-2 bg-[#0055FF]/10 border border-[#0055FF]/25 rounded-lg px-3 py-1.5 text-xs font-mono text-slate-200" data-testid={`env-key-${k}`}>
                {k} <span className="text-slate-600">••••</span>
                <button onClick={() => delEnv(k)} className="text-slate-500 hover:text-red-400" data-testid={`env-del-${k}`}><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        )}
        {envKeys.length > 0 && providers.some((p) => p.connected && p.deployable) && (
          <div className="mt-4 pt-3 border-t border-white/10" data-testid="env-sync-row">
            <p className="text-slate-500 text-[11px] mb-2">Auto-synced on every deploy. Push now to a connected provider's store:</p>
            <div className="flex flex-wrap gap-2">
              {providers.filter((p) => p.connected && p.deployable).map((p) => (
                <button key={p.key} onClick={() => syncEnv(p.key, p.name)} disabled={syncing === p.key} className="inline-flex items-center gap-1.5 border border-white/15 text-xs px-3 py-1.5 rounded-lg hover:border-[#0055FF] transition-colors duration-300 disabled:opacity-40" data-testid={`env-sync-${p.key}`}>
                  {syncing === p.key ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Sync to {p.name}{p.env_sync ? "" : " (CI)"}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {deployments.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="deploy-history">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Deployments</p>
          {deployments.map((d) => (
            <div key={d.id} className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid={`deployment-${d.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  {d.state === "building" || d.state === "queued" ? <Loader2 size={14} className="animate-spin text-[#00F0FF]" /> : d.state === "live" ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-red-400" />}
                  <span className="text-sm text-white capitalize">{d.provider}</span>
                  {d.environment && <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${d.environment === "preview" ? "bg-purple-500/15 text-purple-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid={`deployment-env-${d.id}`}>{d.environment}</span>}
                  <span className={`font-mono text-[11px] uppercase ${STATE_COLOR[d.state] || "text-slate-400"}`} data-testid={`deployment-state-${d.id}`}>{d.state}</span>
                </div>
                <div className="flex items-center gap-3">
                  {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-[#00F0FF] text-xs inline-flex items-center gap-1" data-testid={`deployment-url-${d.id}`}>{d.url.replace("https://", "")} <ExternalLink size={11} /></a>}
                  {d.environment === "preview" && d.state === "live" && !d.approved && (
                    <button onClick={() => promote(d.id)} disabled={busy === `promote-${d.id}`} className="text-emerald-400 hover:text-emerald-300 text-xs inline-flex items-center gap-1 font-semibold" data-testid={`deployment-promote-${d.id}`}>{busy === `promote-${d.id}` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve & promote</button>
                  )}
                  {d.state === "error" && <button onClick={() => diagnose(d.id)} disabled={busy === `diag-${d.id}`} className="text-[#00F0FF] hover:text-white text-xs inline-flex items-center gap-1 font-semibold" data-testid={`deployment-diagnose-${d.id}`}>{busy === `diag-${d.id}` ? <Loader2 size={12} className="animate-spin" /> : <Stethoscope size={12} />} AI Doctor</button>}
                  {d.state === "error" && <button onClick={() => retry(d.id)} className="text-slate-400 hover:text-white text-xs inline-flex items-center gap-1" data-testid={`deployment-retry-${d.id}`}><RefreshCw size={12} /> Retry</button>}
                  {d.state === "live" && d.environment === "production" && (
                    <>
                      <button onClick={() => checkHealth(d.id)} disabled={busy === `health-${d.id}`} className="text-[#00F0FF] hover:text-white text-xs inline-flex items-center gap-1" data-testid={`deployment-health-${d.id}`}>{busy === `health-${d.id}` ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />} Health</button>
                      <button onClick={() => toggleMonitor(d)} title="Uptime monitoring + email alerts" className={`text-xs inline-flex items-center gap-1 ${d.monitor_enabled ? "text-emerald-400" : "text-slate-400 hover:text-white"}`} data-testid={`deployment-monitor-${d.id}`}>{d.monitor_enabled ? <Bell size={12} /> : <BellOff size={12} />} {d.monitor_enabled ? "Monitoring" : "Monitor"}</button>
                      <button onClick={() => rollback(d.id)} disabled={busy === `rollback-${d.id}`} className="text-amber-400 hover:text-amber-300 text-xs inline-flex items-center gap-1 font-semibold" data-testid={`deployment-rollback-${d.id}`}>{busy === `rollback-${d.id}` ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />} Rollback</button>
                    </>
                  )}
                </div>
              </div>
              {(health[d.id] || d.last_health) && (
                <div className={`mt-2 text-xs font-mono inline-flex items-center gap-2 ${((health[d.id] || d.last_health).status) === "up" ? "text-emerald-400" : "text-red-400"}`} data-testid={`deployment-health-result-${d.id}`}>
                  <Activity size={12} /> {(health[d.id] || d.last_health).status === "up" ? `Up · HTTP ${(health[d.id] || d.last_health).http_status} · ${(health[d.id] || d.last_health).latency_ms}ms` : `Down · ${(health[d.id] || d.last_health).error || (health[d.id] || d.last_health).http_status || "no response"}`}
                </div>
              )}
              {d.error && <p className="text-red-400 text-xs mt-2">{d.error}</p>}
              {d.logs?.length > 0 && <pre className="text-[10px] font-mono text-slate-500 max-h-24 overflow-auto mt-2">{d.logs.join("\n")}</pre>}
              {diagnoses[d.id] && (
                <div className="mt-3 rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-3" data-testid={`doctor-${d.id}`}>
                  <p className="font-semibold text-[#00F0FF] text-xs flex items-center gap-1.5 mb-1"><Stethoscope size={13} /> AI Deployment Doctor {diagnoses[d.id].diagnosis.from_history && <span className="text-slate-500">(learned from a past failure)</span>}</p>
                  <p className="text-slate-200 text-sm">{diagnoses[d.id].diagnosis.plain_english}</p>
                  <p className="text-slate-400 text-xs mt-1"><b>Root cause:</b> {diagnoses[d.id].diagnosis.root_cause}</p>
                  <ul className="mt-2 space-y-1">
                    {(diagnoses[d.id].diagnosis.suggested_fixes || []).map((f, i) => (
                      <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5">{f.safe ? <ShieldCheck size={12} className="text-emerald-400 mt-0.5" /> : <span className="text-slate-500 mt-0.5">•</span>}<span><b>{f.title}:</b> {f.description}</span></li>
                    ))}
                  </ul>
                  {diagnoses[d.id].can_auto_fix && (
                    <button onClick={() => autofix(d.id)} disabled={busy === `fix-${d.id}`} className="mt-3 inline-flex items-center gap-1.5 bg-emerald-500 text-black text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 hover:bg-emerald-400 transition-colors duration-300" data-testid={`doctor-autofix-${d.id}`}>{busy === `fix-${d.id}` ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} One-click repair &amp; retry</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
